/**
 * Consultant Client List Page
 *
 * Displays list of clients assigned to the consultant with:
 * - Search and filter capabilities
 * - Client status indicators
 * - Pre-audit status tracking
 * - Quick actions for each client
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/forms/Input';
import { Select } from '../../components/forms/Select';
import { Table, Pagination } from '../../components/common/Table';
import { formatDate } from '../../utils/formatting';
import { mockApi } from '../../services/mockApi';
import type { Application } from '../../types/application';

export const ConsultantClientListPage: React.FC = () => {
  const { t } = useTranslation(['common', 'consultant']);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getAllApplications();
      if (response.success && response.data) {
        // Filter to only applications assigned to consultant
        const consultantClients = response.data.filter(a =>
          ['CONSULTANT_ASSIGNED', 'PRE_AUDIT_SCHEDULED', 'PRE_AUDIT_COMPLETED', 'UNDER_REVIEW'].includes(a.status)
        );
        setApplications(consultantClients);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter applications
  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.applicantName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const paginatedApplications = filteredApplications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'PRE_AUDIT_COMPLETED':
        return 'success';
      case 'CONSULTANT_ASSIGNED':
      case 'PRE_AUDIT_SCHEDULED':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'UNDER_REVIEW':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPreAuditStatus = (application: Application): string => {
    switch (application.status) {
      case 'CONSULTANT_ASSIGNED':
        return t('consultant:status.notStarted');
      case 'PRE_AUDIT_SCHEDULED':
        return t('consultant:status.scheduled');
      case 'PRE_AUDIT_COMPLETED':
        return t('consultant:status.completed');
      default:
        return t('consultant:status.notStarted');
    }
  };

  const handleViewClient = (id: string) => {
    window.location.href = `/consultant/clients/${id}`;
  };

  const handleStartPreAudit = (id: string) => {
    window.location.href = `/consultant/pre-audit/${id}`;
  };

  const columns = [
    {
      key: 'companyName',
      header: t('consultant:clients.companyName'),
      render: (app: Application) => (
        <div>
          <div className="font-medium text-gray-800">{app.companyName}</div>
          <div className="text-sm text-gray-500">{app.type}</div>
        </div>
      ),
    },
    {
      key: 'contactPerson',
      header: t('consultant:clients.contactPerson'),
      render: (app: Application) => (
        <div>
          <div className="text-gray-800">{app.applicantName}</div>
          <div className="text-sm text-gray-500">Contact Info</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('consultant:clients.status'),
      render: (app: Application) => (
        <Badge variant={getStatusVariant(app.status)}>
          {app.status.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'preAuditStatus',
      header: t('consultant:clients.preAuditStatus'),
      render: (app: Application) => (
        <span className="text-sm text-gray-700">{getPreAuditStatus(app)}</span>
      ),
    },
    {
      key: 'assignedDate',
      header: t('consultant:clients.assignedDate'),
      render: (app: Application) => (
        <span className="text-sm text-gray-700">{formatDate(app.createdAt)}</span>
      ),
    },
    {
      key: 'lastActivity',
      header: t('consultant:clients.lastActivity'),
      render: (app: Application) => (
        <span className="text-sm text-gray-700">{formatDate(app.updatedAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: t('common:common.details'),
      render: (app: Application) => (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleViewClient(app.id)}>
            {t('common:actions.view')}
          </Button>
          {app.status === 'CONSULTANT_ASSIGNED' && (
            <Button size="sm" onClick={() => handleStartPreAudit(app.id)}>
              {t('consultant:preAudit.startPreAudit')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: 'ALL', label: t('consultant:clients.allStatuses') },
    { value: 'CONSULTANT_ASSIGNED', label: 'Assigned' },
    { value: 'PRE_AUDIT_SCHEDULED', label: 'Pre-Audit Scheduled' },
    { value: 'PRE_AUDIT_COMPLETED', label: 'Pre-Audit Completed' },
    { value: 'UNDER_REVIEW', label: 'Under Review' },
  ];

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('consultant:clients.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
          { label: t('consultant:clients.title') },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('common:app.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('consultant:clients.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
        { label: t('consultant:clients.title') },
      ]}
    >
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Total Clients</p>
            <p className="text-2xl font-bold text-gray-800">{applications.length}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Assigned</p>
            <p className="text-2xl font-bold text-orange-600">
              {applications.filter(a => a.status === 'CONSULTANT_ASSIGNED').length}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">
              {applications.filter(a => a.status === 'PRE_AUDIT_SCHEDULED').length}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Pre-Audit Completed</p>
            <p className="text-2xl font-bold text-green-600">
              {applications.filter(a => a.status === 'PRE_AUDIT_COMPLETED').length}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="text"
              placeholder={t('consultant:clients.searchClients')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={statusOptions}
            />
          </div>
        </CardBody>
      </Card>

      {/* Client List Table */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">{t('consultant:clients.clientList')}</h3>
        </CardHeader>
        <CardBody>
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('consultant:clients.noClients')}</p>
            </div>
          ) : (
            <>
              <Table
                data={paginatedApplications}
                columns={columns}
                keyExtractor={(app) => app.id}
                isLoading={loading}
              />
              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};
